import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { Observable, firstValueFrom } from "rxjs";

import { NotificationsService } from "./notifications.service";

@Injectable({ providedIn: "root" })
export class NotificationAttachmentDownloadService {
  constructor(
    private readonly notifications: NotificationsService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  downloadFromNotification(
    notificationId: string,
    attachmentIndex: number,
    fileName: string,
  ): Promise<boolean> {
    return this.download(
      () => this.notifications.downloadNotificationAttachment(notificationId, attachmentIndex),
      fileName,
    );
  }

  downloadFromDispatch(
    dispatchId: string,
    attachmentIndex: number,
    fileName: string,
  ): Promise<boolean> {
    return this.download(
      () => this.notifications.downloadDispatchAttachment(dispatchId, attachmentIndex),
      fileName,
    );
  }

  async openNotificationDocument(notificationId: string, attachmentIndex: number): Promise<boolean> {
    return this.openDocument(
      () => this.notifications.downloadNotificationAttachment(notificationId, attachmentIndex),
    );
  }

  async openDispatchDocument(dispatchId: string, attachmentIndex: number): Promise<boolean> {
    return this.openDocument(
      () => this.notifications.downloadDispatchAttachment(dispatchId, attachmentIndex),
    );
  }

  private async openDocument(request: () => Observable<Blob | null>): Promise<boolean> {
    const previewWindow = this.document.defaultView?.open('about:blank', '_blank');
    if (!previewWindow) return false;

    previewWindow.opener = null;
    const blob = await this.fetchBlob(request);

    if (!blob) {
      previewWindow.close();
      return false;
    }

    const objectUrl = URL.createObjectURL(blob);
    previewWindow.location.replace(objectUrl);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    return true;
  }

  private async download(request: () => Observable<Blob | null>, fileName: string): Promise<boolean> {
    const blob = await this.fetchBlob(request);
    if (!blob) return false;

    return this.triggerDownload(blob, fileName);
  }

  private async fetchBlob(request: () => Observable<Blob | null>): Promise<Blob | null> {
    try {
      return await firstValueFrom(request());
    } catch {
      return null;
    }
  }

  private triggerDownload(blob: Blob, fileName: string): boolean {
    let objectUrl: string | null = null;
    let anchor: HTMLAnchorElement | null = null;

    try {
      objectUrl = URL.createObjectURL(blob);
      anchor = this.document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName.trim() || "archivo";
      anchor.rel = "noopener noreferrer";
      this.document.body.appendChild(anchor);
      anchor.click();
      return true;
    } catch {
      return false;
    } finally {
      anchor?.remove();

      if (objectUrl) {
        const urlToRevoke = objectUrl;
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 1000);
      }
    }
  }
}
